import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';


const fadeInLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeInRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const SectionContainer = styled(Box)({
  padding: '140px 40px',
  background: 'linear-gradient(180deg, #0a0a0f 0%, #12121a 100%)',
  position: 'relative',
  overflow: 'hidden',
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const GlowOrb = styled(Box)({
  position: 'absolute',
  width: '500px',
  height: '500px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(249, 115, 22, 0.08) 0%, transparent 70%)',
  top: '10%',
  right: '-10%',
  pointerEvents: 'none'
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '80px',
  alignItems: 'center',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: '1fr',
    gap: '60px'
  }
});

const LeftPanel = styled(Box)({
  animation: `${fadeInLeft} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 20px',
  background: 'rgba(249, 115, 22, 0.1)',
  border: '1px solid rgba(249, 115, 22, 0.2)',
  borderRadius: '100px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 600,
  color: '#f97316',
  textTransform: 'uppercase',
  letterSpacing: '1.5px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: 'clamp(36px, 5vw, 52px)',
  fontWeight: 700,
  color: '#ffffff',
  letterSpacing: '-1px',
  marginBottom: '24px',
  lineHeight: 1.1
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #f97316 0%, #00d4ff 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const Description = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '17px',
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.8,
  marginBottom: '40px'
});

const FeaturesList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
});

const FeatureItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '16px 20px',
  background: 'rgba(255, 255, 255, 0.02)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: '14px',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.04)',
    borderColor: 'rgba(249, 115, 22, 0.3)',
    transform: 'translateX(8px)'
  }
});

const FeatureIcon = styled(CheckCircleIcon)({
  fontSize: '22px',
  color: '#f97316'
});

const FeatureText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 500,
  color: '#ffffff'
});

const RightPanel = styled(Box)({
  animation: `${fadeInRight} 0.8s ease-out`,
  animationDelay: '0.2s',
  animationFillMode: 'backwards'
});

const ImageWrapper = styled(Box)({
  position: 'relative',
  borderRadius: '28px',
  overflow: 'hidden',
  aspectRatio: '4/3',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '-2px',
    borderRadius: '30px',
    padding: '2px',
    background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.4), rgba(0, 212, 255, 0.4))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    zIndex: 1
  }
});

const AboutImage = styled(Box)({
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center'
});

const ImageOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(135deg, rgba(10, 10, 15, 0.3) 0%, rgba(10, 10, 15, 0.6) 100%)'
});





const AboutSection = ({ businessInfo = {}, contactInfo = {} }) => {
  // Helper to extract URL from various image object formats
  const getImageUrl = (image) => {
    if (!image) return null;
    if (typeof image === 'string') return image;
    return image.url || image.googleUrl || image.sourceUrl || image.image || image.imageUrl || image.thumbnailUrl || null;
  };

  const imageUrl = getImageUrl(businessInfo?.heroImage) ||
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80';

  return (
    <SectionContainer id="about">
      <GlowOrb />
      <ContentWrapper>
        <LeftPanel>
          <SectionBadge>
            <BadgeText>About Us</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Building Trust Through<br />
            <GradientText>Excellence</GradientText>
          </SectionTitle>
          <Description>
            {businessInfo?.description ||
              'We are passionate about delivering exceptional results. Our commitment to quality and innovation drives everything we do, ensuring our clients receive nothing but the best.'}
          </Description>

        </LeftPanel>

        <RightPanel>
          <ImageWrapper>
            <AboutImage sx={{ backgroundImage: `url(${imageUrl})` }} />
            <ImageOverlay />
          </ImageWrapper>

        </RightPanel>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default AboutSection;
